import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { FormlyDropdownValue } from '@intellio/shared/models';
import { AnalyticsService } from '@intellio/shared/services';
import { FieldTypeConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material/form-field';
import { Observable, Subject } from 'rxjs';
import { isEmpty, map, startWith, takeUntil } from 'rxjs/operators';
import { FormlyComponentTelemetry } from './formly-component-telemetry';

@Component({
  selector: 'itc-formly-application-type',
  template: `
    @if (props.useTypeahead) {
      <mat-form-field class="full-width">
        <mat-label>{{ props.label }} {{ props.required ? '*' : '' }}</mat-label>
        <input
          type="text"
          matInput
          [formControl]="control"
          [matAutocomplete]="auto"
          />
          <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn">
            @for (option of filteredOptions | async; track option) {
              <mat-option
                [value]="option.value"
                (onSelectionChange)="dropdownSelectionMade(option)"
                >
                {{ option?.label }}
              </mat-option>
            }
          </mat-autocomplete>
        </mat-form-field>
      } @else {
        @if ((hasNoValues | async) === false) {
          <strong>{{
            props.label
          }}</strong>
        } @else {
          <strong>
            Based on your current selections, no applicable application types were
            found.
          </strong>
        }
        {{ props.required ? ' *' : '' }}
        <mat-radio-group (change)="selectionMade($event.source.value)">
          @for (option of ((props.applicationTypes$ | async) ?? []); track option) {
            <div>
              <mat-radio-button [value]="option.value" color="primary">
                <strong>{{ option?.label }}</strong>
                {{ ' ' + option?.detail }}
              </mat-radio-button>
            </div>
          }
        </mat-radio-group>
      }
    
    `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      .full-width {
        width: 100%;
      }
    `,
  ],
  imports: [
    MatRadioModule,
    MatOptionModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatLabel,
    MatFormFieldModule,
    CommonModule,
    MatInputModule,
  ],
})
export class ApplicationTypeTypeComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, OnDestroy
{
  constructor(
    protected cd: ChangeDetectorRef,
    private analyticsService: AnalyticsService
  ) {
    super();
  }

  control: UntypedFormControl;
  onDestroy$ = new Subject<void>();
  results: FormlyDropdownValue[];
  jsonOptions: any[];
  hasNoValues: Observable<boolean>;
  filteredOptions: Observable<any[]>;
  displayValue: string; // Separate property to store display value
  private telemetry = new FormlyComponentTelemetry(
    (eventName, properties) => this.analyticsService.logEvent(eventName, properties),
    () => ({ fieldKey: this.field?.key?.toString() })
  );

  displayFn = (val) => {
    return this.displayValue || val;
  };

  ngOnInit() {
    this.telemetry.mark('init');
    this.control = this.formControl as UntypedFormControl;
    this.hasNoValues = this.props.applicationTypes$.pipe(isEmpty());

    this.setupDynamicOptions();
  }

  setupDynamicOptions() {
    this.props.applicationTypes$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((options) => {
      this.results = options;
      this.telemetry.event('FormlyPerf.ApplicationTypeOptionsLoaded', {
        durationMs: this.telemetry.elapsedMs('init') ?? 0,
        optionCount: options?.length ?? 0,
      });
      this.filteredOptions = this.control.valueChanges.pipe(
        startWith(''),
        map((search) => {
          if (typeof search !== 'string') {
            return this.results;
          }
          const lowerSearch = search.toLowerCase();
          this.telemetry.mark('search-input');
          return this.results.filter(
            (option) =>
              option.label.toLowerCase().includes(lowerSearch) ||
              option.value.toLowerCase().includes(lowerSearch)
          );
        })
      );
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  selectionMade(option) {
    this.telemetry.event('FormlyPerf.ApplicationTypeSelection', {
      via: 'radio',
      value: option,
    });
    this.control.setValue(option);
  }

  dropdownSelectionMade(option) {
    this.telemetry.event('FormlyPerf.ApplicationTypeSelection', {
      via: 'typeahead',
      value: option?.value,
      searchToSelectMs: this.telemetry.elapsedMs('search-input'),
    });
    this.control.setValue(option.value);
    this.displayValue = option.label; // Update the display value
    this.cd.detectChanges(); // Trigger change detection
  }
}
